import React from "react";
import { Switch, Route } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import AppShell from "@/components/layout/AppShell";
import RequireAuth from "@/components/RequireAuth";
import RequireRole from "@/components/RequireRole";
import { DioceseScopeProvider } from "@/lib/diocese-scope";
import { AnneeScolaireProvider } from "@/lib/annee-scope";

import Landing from "@/pages/landing";
import EcolesList from "@/pages/ecoles/index";
import EcoleDetail from "@/pages/ecoles/detail";
import Payer from "@/pages/payer/index";
import PaiementDetail from "@/pages/paiement/detail";
import PaiementSuccess from "@/pages/paiement/success";
import PaiementFailed from "@/pages/paiement/failed";
import Login from "@/pages/auth/login";
import Register from "@/pages/auth/register";
import Otp from "@/pages/auth/otp";
import Dashboard from "@/pages/dashboard/index";
import NotFound from "@/pages/not-found";

// ── Back-office (S4) ────────────────────────────────────────────────────
import EcoleDashboard from "@/pages/ecole/index";
import EcoleMotifs from "@/pages/ecole/motifs";
import EcoleClasses from "@/pages/ecole/classes";
import EcoleEleves from "@/pages/ecole/eleves";
import EcoleDemandes from "@/pages/ecole/demandes";
import EcoleParents from "@/pages/ecole/parents";
import EcoleEleveDetail from "@/pages/ecole/eleve-detail";
import EcolePaiements from "@/pages/ecole/paiements";
import EcolePaiementsHorsSysteme from "@/pages/ecole/paiements-hors-systeme";
import EcoleCompteReversement from "@/pages/ecole/compte-reversement";
import EcoleSaisie from "@/pages/ecole/saisie";
import EconomatDashboard from "@/pages/economat/index";
import EconomatEcoles from "@/pages/economat/ecoles";
import EconomatEcoleDetail from "@/pages/economat/ecole-detail";
import EconomatPaiements from "@/pages/economat/paiements";
import EconomatAudit from "@/pages/economat/audit";
import SedecUsers from "@/pages/sedec/index";
import SedecEcoles from "@/pages/sedec/ecoles";
import SedecComptesReversement from "@/pages/sedec/comptes-reversement";
import SedecSuivi from "@/pages/sedec/suivi";
import SedecVitrines from "@/pages/sedec/vitrines";
import SedecAnnees from "@/pages/sedec/annees";
import SedecAudit from "@/pages/sedec/audit";
import SedecDiffusion from "@/pages/sedec/diffusion";

// Role groups — accept both prototype UPPERCASE labels and the monorepo
// lowercase canonical forms (RequireRole normalizes both sides).
const ECOLE_ROLES = ["ECOLE_ECONOME", "ECOLE_CAISSE", "econome", "econome_etab", "caisse"];
const ECONOMAT_ROLES = [
  "DIOCESE_ECONOMAT", "DIOCESE_AUDITEUR",
  "econome_diocesain", "auditeur_diocesain",
];
const SEDEC_ROLES = [
  "DIOCESE_SEDEC_ADMIN", "DIOCESE_SUPER_ADMIN",
  "sedec_admin", "school_admin", "super_admin", "admin",
];

function ScopedEcole({ children }: { children: React.ReactNode }) {
  return (
    <RequireRole roles={ECOLE_ROLES}>
      <AnneeScolaireProvider>{children}</AnneeScolaireProvider>
    </RequireRole>
  );
}

function ScopedEconomat({ children }: { children: React.ReactNode }) {
  return (
    <RequireRole roles={ECONOMAT_ROLES}>
      <DioceseScopeProvider>{children}</DioceseScopeProvider>
    </RequireRole>
  );
}

function ScopedSedec({ children }: { children: React.ReactNode }) {
  return (
    <RequireRole roles={SEDEC_ROLES}>
      <DioceseScopeProvider>{children}</DioceseScopeProvider>
    </RequireRole>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Switch>
          {/* Landing : AppShell fournit la nav transparente (logo seul) au-dessus
              du héro plein cadre, puis blanche au scroll. */}
          <Route path="/">
            <AppShell>
              <Landing />
            </AppShell>
          </Route>

          {/* Back-office: chaque espace porte son propre shell — pas d'AppShell. */}
          <Route path="/ecole">
            <ScopedEcole><EcoleDashboard /></ScopedEcole>
          </Route>
          <Route path="/ecole/motifs">
            <ScopedEcole><EcoleMotifs /></ScopedEcole>
          </Route>
          <Route path="/ecole/classes">
            <ScopedEcole><EcoleClasses /></ScopedEcole>
          </Route>
          <Route path="/ecole/eleves">
            <ScopedEcole><EcoleEleves /></ScopedEcole>
          </Route>
          <Route path="/ecole/demandes">
            <ScopedEcole><EcoleDemandes /></ScopedEcole>
          </Route>
          <Route path="/ecole/parents">
            <ScopedEcole><EcoleParents /></ScopedEcole>
          </Route>
          <Route path="/ecole/eleves/:eleveId">
            <ScopedEcole><EcoleEleveDetail /></ScopedEcole>
          </Route>
          <Route path="/ecole/paiements">
            <ScopedEcole><EcolePaiements /></ScopedEcole>
          </Route>
          <Route path="/ecole/paiements-hors-systeme">
            <ScopedEcole><EcolePaiementsHorsSysteme /></ScopedEcole>
          </Route>
          <Route path="/ecole/compte-reversement">
            <ScopedEcole><EcoleCompteReversement /></ScopedEcole>
          </Route>
          <Route path="/ecole/saisie">
            <ScopedEcole><EcoleSaisie /></ScopedEcole>
          </Route>

          <Route path="/economat">
            <ScopedEconomat><EconomatDashboard /></ScopedEconomat>
          </Route>
          <Route path="/economat/ecoles">
            <ScopedEconomat><EconomatEcoles /></ScopedEconomat>
          </Route>
          <Route path="/economat/ecoles/:ecoleId">
            <ScopedEconomat><EconomatEcoleDetail /></ScopedEconomat>
          </Route>
          <Route path="/economat/paiements">
            <ScopedEconomat><EconomatPaiements /></ScopedEconomat>
          </Route>
          <Route path="/economat/audit">
            <ScopedEconomat><EconomatAudit /></ScopedEconomat>
          </Route>

          <Route path="/sedec">
            <ScopedSedec><SedecUsers /></ScopedSedec>
          </Route>
          <Route path="/sedec/suivi">
            <ScopedSedec><SedecSuivi /></ScopedSedec>
          </Route>
          <Route path="/sedec/ecoles">
            <ScopedSedec><SedecEcoles /></ScopedSedec>
          </Route>
          <Route path="/sedec/comptes-reversement">
            <ScopedSedec><SedecComptesReversement /></ScopedSedec>
          </Route>
          <Route path="/sedec/vitrines">
            <ScopedSedec><SedecVitrines /></ScopedSedec>
          </Route>
          <Route path="/sedec/annees">
            <ScopedSedec><SedecAnnees /></ScopedSedec>
          </Route>
          <Route path="/sedec/audit">
            <ScopedSedec><SedecAudit /></ScopedSedec>
          </Route>
          <Route path="/sedec/diffusion">
            <ScopedSedec><SedecDiffusion /></ScopedSedec>
          </Route>

          {/* All other parent routes share the AppShell. */}
          <Route>
            <AppShell>
              <Switch>
                <Route path="/ecoles" component={EcolesList} />
                <Route path="/ecoles/:ecoleId" component={EcoleDetail} />

                <Route path="/payer" component={Payer} />
                <Route path="/payer/:ecoleId" component={Payer} />

                <Route path="/paiement/success" component={PaiementSuccess} />
                <Route path="/paiement/failed" component={PaiementFailed} />
                <Route
                  path="/paiement/:paiementId/success"
                  component={PaiementSuccess}
                />
                <Route
                  path="/paiement/:paiementId/failed"
                  component={PaiementFailed}
                />
                <Route path="/paiement/:paiementId" component={PaiementDetail} />

                <Route path="/auth/login" component={Login} />
                <Route path="/auth/register" component={Register} />
                <Route path="/auth/otp" component={Otp} />

                <Route path="/dashboard">
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                </Route>

                <Route component={NotFound} />
              </Switch>
            </AppShell>
          </Route>
        </Switch>
        <Toaster richColors position="top-right" />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
